import React from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, MapPin, ShieldCheck, ArrowRight } from "lucide-react";

const AVAIL_STYLE = {
  available: { text: "Available now", class: "bg-[#E6F4EC] text-[#3F8F5D] border-[#CCE5D6]" },
  limited: { text: "Limited rooms", class: "bg-[#F3EEFA] text-[#553180] border-[#E8DFF5]" },
  waitlist: { text: "Join waitlist", class: "bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-800)] border-[color:var(--alpha-line)]" },
};

export default function VacancyCard({ vacancy }) {
  const a = AVAIL_STYLE[vacancy.availability] || AVAIL_STYLE.available;
  return (
    <Link
      to={`/vacancies/${vacancy.id}`}
      data-testid={`vacancy-card-${vacancy.id}`}
      className="alpha-card group overflow-hidden flex flex-col focus:outline-none alpha-focus"
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={vacancy.hero_image}
          alt={vacancy.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14322F]/55 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="alpha-chip alpha-chip !bg-white/90">{vacancy.service_type}</span>
          {vacancy.sda_category && (
            <span className="alpha-chip alpha-chip-green">{vacancy.sda_category}</span>
          )}
        </div>
        <div className={`absolute top-3 right-3 text-[11px] font-semibold border rounded-full px-2.5 py-1 ${a.class}`}>
          {a.text}
        </div>
        <div className="absolute bottom-3 left-3 text-white/95 text-xs flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" /> {vacancy.suburb}, {vacancy.state}
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-semibold text-lg leading-snug text-[color:var(--alpha-ink)] group-hover:text-[color:var(--alpha-purple-700)] transition-colors">
          {vacancy.title}
        </h3>
        <div className="mt-3 flex items-center gap-4 text-sm text-[color:var(--alpha-mute)]">
          <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4" /> {vacancy.bedrooms} bed</span>
          <span className="flex items-center gap-1.5"><Bath className="w-4 h-4" /> {vacancy.bathrooms} bath</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> {vacancy.accessibility_features?.length || 0} access feat.</span>
        </div>
        {vacancy.accessibility_features?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {vacancy.accessibility_features.slice(0, 3).map((f) => (
              <span key={f} className="alpha-chip-neutral alpha-chip !text-[11px] !py-1 !px-2">
                {f}
              </span>
            ))}
            {vacancy.accessibility_features.length > 3 && (
              <span className="alpha-chip-neutral alpha-chip !text-[11px] !py-1 !px-2">+{vacancy.accessibility_features.length - 3}</span>
            )}
          </div>
        )}
        <div className="mt-auto pt-5 flex items-center justify-between border-t border-[color:var(--alpha-line)]/70 mt-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--alpha-mute)]">From</div>
            <div className="font-display font-semibold text-[color:var(--alpha-ink)]">${vacancy.price_from}<span className="text-xs font-normal text-[color:var(--alpha-mute)]">/week*</span></div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:gap-2 transition-all">
            View home <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
